import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { LocalNotifications } from '@capacitor/local-notifications';

/**
 * Check if running on native mobile platform
 */
const isNativePlatform = () => {
  return Capacitor.isNativePlatform();
};

/**
 * Generate filename with current datetime
 * Format: hisab-journey-ledger-YYYYMMDDHHMMSS
 */
const generateFilename = (extension) => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  const datetime = `${year}${month}${day}${hours}${minutes}${seconds}`;
  return `hisab-journey-ledger-${datetime}.${extension}`;
};

/**
 * Check notification permission status
 */
export const checkNotificationPermission = async () => {
  if (!isNativePlatform()) return { granted: true };

  try {
    const result = await LocalNotifications.checkPermissions();
    return { granted: result.display === 'granted', status: result.display };
  } catch (error) {
    console.error('Error checking notification permission:', error);
    return { granted: false, status: 'denied' };
  }
};

/**
 * Request notification permission
 * @returns {Promise<{granted: boolean, status: string}>}
 */
export const requestNotificationPermission = async () => {
  if (!isNativePlatform()) return { granted: true, status: 'granted' };

  try {
    const result = await LocalNotifications.requestPermissions();
    return { granted: result.display === 'granted', status: result.display };
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return { granted: false, status: 'denied' };
  }
};

/**
 * Save file on mobile using Capacitor Filesystem
 * @param {string} filename - Name of the file
 * @param {Blob|ArrayBuffer|string} data - File data
 * @param {string} mimeType - MIME type of the file
 */
const saveFileOnMobile = async (filename, data, mimeType) => {
  try {
    console.log('[Export] saveFileOnMobile called for:', filename);
    console.log('[Export] Data type:', typeof data, 'Constructor:', data?.constructor?.name);

    // Convert data to base64
    let base64Data;

    if (data instanceof Blob) {
      console.log('[Export] Converting Blob to base64, size:', data.size);
      base64Data = await blobToBase64(data);
    } else if (data instanceof ArrayBuffer) {
      console.log('[Export] Converting ArrayBuffer to base64, byteLength:', data.byteLength);
      base64Data = arrayBufferToBase64(data);
    } else if (ArrayBuffer.isView(data)) {
      // Handle Uint8Array and other typed arrays (returned by XLSX.write with type: 'array')
      console.log('[Export] Converting TypedArray to base64, byteLength:', data.byteLength);
      base64Data = typedArrayToBase64(data);
    } else if (typeof data === 'string') {
      console.log('[Export] Converting string to base64, length:', data.length);
      base64Data = btoa(unescape(encodeURIComponent(data)));
    } else if (Array.isArray(data) || (data && typeof data.length === 'number')) {
      // Handle array-like objects
      console.log('[Export] Converting array-like to base64, length:', data.length);
      base64Data = typedArrayToBase64(new Uint8Array(data));
    } else {
      console.error('[Export] Unknown data type:', typeof data, data?.constructor?.name);
      throw new Error(`Unsupported data type: ${typeof data}`);
    }

    console.log('[Export] Base64 data length:', base64Data?.length);

    // Save to Downloads directory
    console.log('[Export] Writing file to Documents directory...');
    const result = await Filesystem.writeFile({
      path: filename,
      data: base64Data,
      directory: Directory.Documents,
      recursive: true
    });

    console.log('[Export] File saved successfully:', result.uri);

    // Show notification
    await showDownloadNotification(filename, result.uri);

    return { success: true, path: result.uri };
  } catch (error) {
    console.error('[Export] Error saving file on mobile:', error);
    console.error('[Export] Error details:', error.message, error.stack);
    throw error;
  }
};

/**
 * Convert Blob to base64 string
 */
const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * Convert ArrayBuffer to base64 string
 */
const arrayBufferToBase64 = (buffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

/**
 * Convert TypedArray (Uint8Array, etc.) to base64 string
 * This is the format returned by XLSX.write with type: 'array'
 */
const typedArrayToBase64 = (typedArray) => {
  let binary = '';
  const len = typedArray.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(typedArray[i]);
  }
  return btoa(binary);
};

/**
 * Show download complete notification on mobile
 */
const showDownloadNotification = async (filename, filePath) => {
  try {
    // Check permission status
    const permResult = await LocalNotifications.checkPermissions();

    if (permResult.display === 'granted') {
      // Use a small random number for the ID (Java int max is 2147483647)
      const notificationId = Math.floor(Math.random() * 2147483647);

      // Get file type from extension
      const extension = filename.split('.').pop()?.toUpperCase() || 'File';

      await LocalNotifications.schedule({
        notifications: [
          {
            title: `${extension} Report Downloaded ✓`,
            body: `Your ledger report is ready! Open Documents folder to view.`,
            id: notificationId,
            schedule: { at: new Date(Date.now() + 100) },
            sound: 'default',
            smallIcon: 'ic_notification',  // Custom notification icon (monochrome)
            largeIcon: 'ic_launcher',  // Full color app icon for expanded view
            iconColor: '#1A365D'
          }
        ]
      });
    }
  } catch (error) {
    console.error('Error showing notification:', error);
  }
};

/**
 * Download file - handles both web and mobile platforms
 */
const downloadFile = async (filename, data, mimeType) => {
  if (isNativePlatform()) {
    // Mobile: Use Capacitor Filesystem
    return await saveFileOnMobile(filename, data, mimeType);
  } else {
    // Web: Use standard blob download
    const blob = data instanceof Blob ? data : new Blob([data], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    return { success: true };
  }
};

/**
 * Convert hex color to RGB array
 */
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : [59, 130, 246]; // Default blue
};

/**
 * Lighten a color by mixing with white
 */
const lightenColor = (rgb, amount = 0.85) => {
  return rgb.map(c => Math.round(c + (255 - c) * amount));
};

/**
 * Darken a color by reducing brightness
 */
const darkenColor = (rgb, amount = 0.2) => {
  return rgb.map(c => Math.round(c * (1 - amount)));
};

/**
 * Get PDF-safe currency prefix based on currency code
 * jsPDF default font doesn't support all Unicode symbols, so we use safe alternatives
 */
const getCurrencyPrefix = (currencyCode) => {
  const currencyMap = {
    'INR': 'Rs. ',
    'USD': '$ ',
    'EUR': 'EUR ',
    'GBP': 'GBP ',
    'JPY': 'Y ',      // Yen - use Y as safe alternative
    'AUD': 'A$ ',
    'CAD': 'C$ ',
    'CHF': 'CHF ',
    'CNY': 'CNY ',
    'HKD': 'HK$ ',
    'SGD': 'S$ ',
    'AED': 'AED ',
    'SAR': 'SAR ',
    'BDT': 'Tk. ',
    'PKR': 'Rs. ',
    'LKR': 'Rs. ',
    'NPR': 'Rs. ',
    'KRW': 'W ',      // Won
    'THB': 'B ',      // Baht
    'MYR': 'RM ',
    'PHP': 'P ',
    'IDR': 'Rp. ',
    'VND': 'D ',
    'BRL': 'R$ ',
    'MXN': 'MX$ ',
    'RUB': 'RUB ',
    'ZAR': 'R ',
    'NZD': 'NZ$ ',
    'SEK': 'kr ',
    'NOK': 'kr ',
    'DKK': 'kr ',
    'PLN': 'zl ',
    'CZK': 'Kc ',
    'HUF': 'Ft ',
    'TRY': 'TL ',
    'ILS': 'ILS ',
    'EGP': 'LE ',
    'NGN': 'N ',
    'KES': 'KSh '
  };
  return currencyMap[currencyCode] || currencyCode + ' ';
};

/**
 * Format number as currency for PDF
 * @param {number} amount - The amount to format
 * @param {string} currencyPrefix - PDF-safe currency prefix (e.g., "Rs.", "$")
 */
const formatPDFAmount = (amount, currencyPrefix = 'Rs.') => {
  if (amount === null || amount === undefined) return '-';
  const num = Number(amount);
  if (isNaN(num)) return '-';

  // Format with Indian number system (lakhs, crores)
  const absNum = Math.abs(num);
  let formatted;

  if (absNum >= 10000000) {
    // Crores
    formatted = (absNum / 10000000).toFixed(2) + ' Cr';
  } else if (absNum >= 100000) {
    // Lakhs
    formatted = (absNum / 100000).toFixed(2) + ' L';
  } else {
    // Regular formatting with commas (Indian style)
    formatted = absNum.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  return `${currencyPrefix}${formatted}`;
};

/**
 * Export ledger data to PDF
 * Uses fixed brand colors matching the app logo/splash screen
 */
export const exportToPDF = async (transactions, summary, filters, formatAmount, formatDate, exportConfig = {}) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // Get config values - use currency code for PDF-safe formatting
  const currencyCode = exportConfig.currencyCode || 'INR';
  const currencyPrefix = getCurrencyPrefix(currencyCode);
  const incomeColorHex = exportConfig.incomeColor || '#10b981';
  const expenseColorHex = exportConfig.expenseColor || '#ef4444';

  // Soft Blue Theme - Light and clean professional look
  // Header: #DBEAFE (light blue bg), Accent: #3B82F6 (blue line), Light: #EFF6FF, Text: #1E40AF (dark blue)
  const headerBgHex = '#DBEAFE';       // Light blue header background
  const accentLineHex = '#3B82F6';     // Blue accent line
  const headerTextHex = '#1E40AF';     // Dark blue text for headers
  const lightBgHex = '#EFF6FF';        // Very light blue for alternate rows

  // Convert colors
  const incomeColor = hexToRgb(incomeColorHex);
  const expenseColor = hexToRgb(expenseColorHex);
  const headerBgColor = hexToRgb(headerBgHex);       // Light blue header bg
  const accentLineColor = hexToRgb(accentLineHex);   // Blue accent line
  const headerTextColor = hexToRgb(headerTextHex);   // Dark blue text
  const incomeColorLight = lightenColor(incomeColor, 0.85);
  const expenseColorLight = lightenColor(expenseColor, 0.85);
  const lightBgColor = hexToRgb(lightBgHex);         // #EFF6FF - light blue for rows

  // ===== HEADER SECTION =====
  // Main header background with light blue color
  doc.setFillColor(...headerBgColor);
  doc.rect(0, 0, pageWidth, 42, 'F');

  // Decorative accent line at bottom of header (using blue accent)
  doc.setFillColor(...accentLineColor);
  doc.rect(0, 42, pageWidth, 3, 'F');

  // App name - dark blue text on light background
  doc.setTextColor(...headerTextColor);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('Hisab Journey', pageWidth / 2, 18, { align: 'center' });

  // Subtitle
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text('Personal Finance Manager', pageWidth / 2, 27, { align: 'center' });

  // Report title with styled badge
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('LEDGER REPORT', pageWidth / 2, 38, { align: 'center' });

  // ===== DATE RANGE & FILTERS =====
  doc.setTextColor(60, 60, 60);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');

  let dateRangeText = 'All Transactions';
  if (filters.startDate && filters.endDate) {
    dateRangeText = `Period: ${formatDate(filters.startDate)} to ${formatDate(filters.endDate)}`;
  } else if (filters.startDate) {
    dateRangeText = `From: ${formatDate(filters.startDate)}`;
  } else if (filters.endDate) {
    dateRangeText = `Until: ${formatDate(filters.endDate)}`;
  }
  doc.text(dateRangeText, pageWidth / 2, 54, { align: 'center' });

  // Account filter info
  if (filters.accountId) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Account: ${filters.accountId}`, pageWidth / 2, 61, { align: 'center' });
  }

  // ===== SUMMARY SECTION =====
  const summaryStartY = filters.accountId ? 70 : 63;

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...headerTextColor);
  doc.text('FINANCIAL SUMMARY', 15, summaryStartY);

  // Summary boxes
  const boxWidth = 43;
  const boxHeight = 22;
  const startX = 15;
  const gap = 3;
  const boxY = summaryStartY + 5;

  // Opening Balance Box
  doc.setFillColor(245, 245, 245);
  doc.roundedRect(startX, boxY, boxWidth, boxHeight, 2, 2, 'F');
  doc.setTextColor(100, 100, 100);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text('Opening Balance', startX + 3, boxY + 7);
  doc.setTextColor(50, 50, 50);
  doc.setFontSize(10);
  doc.text(formatPDFAmount(summary.openingBalance, currencyPrefix), startX + 3, boxY + 16);

  // Total Income Box (no + sign)
  doc.setFillColor(...incomeColorLight);
  doc.roundedRect(startX + boxWidth + gap, boxY, boxWidth, boxHeight, 2, 2, 'F');
  doc.setTextColor(...incomeColor);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text('Total Income', startX + boxWidth + gap + 3, boxY + 7);
  doc.setFontSize(10);
  doc.text(formatPDFAmount(summary.totalIncome, currencyPrefix), startX + boxWidth + gap + 3, boxY + 16);

  // Total Expense Box (no - sign)
  doc.setFillColor(...expenseColorLight);
  doc.roundedRect(startX + (boxWidth + gap) * 2, boxY, boxWidth, boxHeight, 2, 2, 'F');
  doc.setTextColor(...expenseColor);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text('Total Expense', startX + (boxWidth + gap) * 2 + 3, boxY + 7);
  doc.setFontSize(10);
  doc.text(formatPDFAmount(summary.totalExpense, currencyPrefix), startX + (boxWidth + gap) * 2 + 3, boxY + 16);

  // Closing Balance Box - use light blue background
  doc.setFillColor(...lightBgColor);
  doc.roundedRect(startX + (boxWidth + gap) * 3, boxY, boxWidth, boxHeight, 2, 2, 'F');
  doc.setTextColor(...accentLineColor);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text('Closing Balance', startX + (boxWidth + gap) * 3 + 3, boxY + 7);
  doc.setFontSize(10);
  const closingColor = summary.closingBalance >= 0 ? incomeColor : expenseColor;
  doc.setTextColor(...closingColor);
  doc.text(formatPDFAmount(summary.closingBalance, currencyPrefix), startX + (boxWidth + gap) * 3 + 3, boxY + 16);

  // ===== TRANSACTION TABLE =====
  doc.setTextColor(...headerTextColor);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('TRANSACTION DETAILS', 15, boxY + boxHeight + 12);

  // Table data
  const tableData = transactions.map((tx) => [
    formatDate(tx.date),
    (tx.description || '-').substring(0, 40),
    (tx.category || '-').substring(0, 15),
    tx.debit > 0 ? formatPDFAmount(tx.debit, currencyPrefix) : '-',
    tx.credit > 0 ? formatPDFAmount(tx.credit, currencyPrefix) : '-',
    formatPDFAmount(tx.balance, currencyPrefix)
  ]);

  // Add opening balance row
  tableData.unshift([
    filters.startDate ? formatDate(filters.startDate) : '-',
    'Opening Balance',
    '-',
    '-',
    '-',
    formatPDFAmount(summary.openingBalance, currencyPrefix)
  ]);

  // Generate table
  autoTable(doc, {
    startY: boxY + boxHeight + 17,
    head: [['Date', 'Description', 'Category', 'Debit', 'Credit', 'Balance']],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: headerBgColor,
      textColor: headerTextColor,
      fontStyle: 'bold',
      halign: 'center',
      fontSize: 9,
      cellPadding: 3
    },
    columnStyles: {
      0: { cellWidth: 22, halign: 'center' },
      1: { cellWidth: 50 },
      2: { cellWidth: 28, halign: 'center' },
      3: { cellWidth: 28, halign: 'right' },
      4: { cellWidth: 28, halign: 'right' },
      5: { cellWidth: 30, halign: 'right' }
    },
    styles: {
      fontSize: 8,
      cellPadding: 2,
      lineColor: [220, 220, 220],
      lineWidth: 0.1
    },
    alternateRowStyles: {
      fillColor: lightBgColor
    },
    didParseCell: function (data) {
      // Style debit column with expense color
      if (data.column.index === 3 && data.cell.raw !== '-' && data.section === 'body') {
        data.cell.styles.textColor = expenseColor;
        data.cell.styles.fontStyle = 'bold';
      }
      // Style credit column with income color
      if (data.column.index === 4 && data.cell.raw !== '-' && data.section === 'body') {
        data.cell.styles.textColor = incomeColor;
        data.cell.styles.fontStyle = 'bold';
      }
      // Style opening balance row
      if (data.row.index === 0 && data.section === 'body') {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [241, 245, 249];
      }
      // Style balance column
      if (data.column.index === 5 && data.section === 'body') {
        data.cell.styles.fontStyle = 'bold';
      }
    },
    margin: { left: 15, right: 15 }
  });

  // ===== FOOTER ON ALL PAGES =====
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);

    // Footer accent line using accent color
    doc.setDrawColor(...accentLineColor);
    doc.setLineWidth(0.5);
    doc.line(15, pageHeight - 20, pageWidth - 15, pageHeight - 20);

    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'normal');

    doc.text('Hisab Journey - Personal Finance', 15, pageHeight - 12);

    const generatedDate = new Date().toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    doc.text(`Generated: ${generatedDate}`, pageWidth / 2, pageHeight - 12, { align: 'center' });

    doc.text(`Page ${i} of ${pageCount}`, pageWidth - 15, pageHeight - 12, { align: 'right' });
  }

  // Generate filename with datetime format: hisab-journey-ledger-YYYYMMDDHHMMSS.pdf
  const filename = generateFilename('pdf');

  // Save file - handles both web and mobile
  console.log('[Export] PDF export - isNativePlatform:', isNativePlatform());
  if (isNativePlatform()) {
    console.log('[Export] Generating PDF blob for mobile...');
    const pdfBlob = doc.output('blob');
    console.log('[Export] PDF blob size:', pdfBlob.size);
    await downloadFile(filename, pdfBlob, 'application/pdf');
    console.log('[Export] PDF download completed');
  } else {
    doc.save(filename);
  }
};

/**
 * Export ledger data to Excel-compatible CSV
 * Creates a properly formatted spreadsheet with consistent columns
 */
export const exportToCSV = (transactions, summary, filters, formatAmount, formatDate, exportConfig = {}) => {
  const currencyCode = exportConfig.currencyCode || 'INR';
  const currencyPrefix = getCurrencyPrefix(currencyCode);

  // Format amount for CSV (plain number for calculations)
  const formatCSVAmount = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '';
    const num = Number(amount);
    if (isNaN(num)) return '';
    return num.toFixed(2);
  };

  // Use user's date format from config (passed as formatDate parameter)
  const formatExportDate = (dateStr) => {
    if (!dateStr) return '';
    return formatDate(dateStr);
  };

  // Format time properly
  const formatExcelTime = (timeStr) => {
    if (!timeStr) return '';
    return timeStr;
  };

  // Date range text
  let dateRangeText = 'All Transactions';
  if (filters.startDate && filters.endDate) {
    dateRangeText = `${formatExportDate(filters.startDate)} to ${formatExportDate(filters.endDate)}`;
  } else if (filters.startDate) {
    dateRangeText = `From ${formatExportDate(filters.startDate)}`;
  } else if (filters.endDate) {
    dateRangeText = `Until ${formatExportDate(filters.endDate)}`;
  }

  // Generated date/time using user's date format
  const now = new Date();
  const generatedDate = formatDate(now);
  const generatedTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

  // Define consistent column count (9 columns to match transaction data)
  const COLS = 9;
  const emptyRow = Array(COLS).fill('');
  const padRow = (arr) => {
    const row = [...arr];
    while (row.length < COLS) row.push('');
    return row;
  };

  // Build CSV data array with consistent columns
  const data = [];

  // ===== HEADER SECTION =====
  data.push(padRow(['HISAB JOURNEY - LEDGER REPORT']));
  data.push(padRow(['Personal Finance Manager']));
  data.push(emptyRow);

  // Report info in two columns
  data.push(padRow(['Report Period:', dateRangeText]));
  data.push(padRow(['Account:', filters.accountId || 'All Accounts']));
  data.push(padRow(['Currency:', currencyPrefix.trim()]));
  data.push(padRow(['Generated Date:', generatedDate]));
  data.push(padRow(['Generated Time:', generatedTime]));
  data.push(emptyRow);

  // ===== SUMMARY SECTION =====
  data.push(padRow(['FINANCIAL SUMMARY']));
  data.push(padRow(['Opening Balance:', formatCSVAmount(summary.openingBalance)]));
  data.push(padRow(['Total Income:', formatCSVAmount(summary.totalIncome)]));
  data.push(padRow(['Total Expense:', formatCSVAmount(summary.totalExpense)]));
  data.push(padRow(['Net Change:', formatCSVAmount(summary.totalIncome - summary.totalExpense)]));
  data.push(padRow(['Closing Balance:', formatCSVAmount(summary.closingBalance)]));
  data.push(emptyRow);

  // ===== TRANSACTION TABLE =====
  data.push(padRow([`TRANSACTION DETAILS (${transactions.length} records)`]));
  data.push(emptyRow);

  // Table header row
  data.push(['Date', 'Time', 'Description', 'Category', 'Account', 'Type', 'Debit', 'Credit', 'Balance']);

  // Opening balance row
  data.push([
    filters.startDate ? formatExportDate(filters.startDate) : '',
    '',
    'Opening Balance',
    '',
    '',
    '',
    '',
    '',
    formatCSVAmount(summary.openingBalance)
  ]);

  // Transaction rows
  transactions.forEach((tx) => {
    const accountDisplay = tx.type === 'transfer'
      ? `${tx.bankName || ''} to ${tx.toAccount || ''}`
      : (tx.bankName || '');

    data.push([
      formatExportDate(tx.date),
      formatExcelTime(tx.time),
      tx.description || '',
      tx.category || '',
      accountDisplay,
      tx.type || '',
      tx.debit > 0 ? formatCSVAmount(tx.debit) : '',
      tx.credit > 0 ? formatCSVAmount(tx.credit) : '',
      formatCSVAmount(tx.balance)
    ]);
  });

  // Closing balance row
  data.push([
    filters.endDate ? formatExportDate(filters.endDate) : formatDate(new Date()),
    '',
    'Closing Balance',
    '',
    '',
    '',
    '',
    '',
    formatCSVAmount(summary.closingBalance)
  ]);

  data.push(emptyRow);

  // ===== FOOTER =====
  data.push(padRow(['END OF REPORT']));
  data.push(padRow(['Generated by Hisab Journey - Personal Finance Manager']));

  // Convert to CSV string
  // Use quotes only where needed (fields with commas, quotes, or newlines)
  const csv = Papa.unparse(data, {
    quotes: false,  // Only quote when necessary
    quoteChar: '"',
    escapeChar: '"',
    delimiter: ',',
    newline: '\r\n'  // Windows-compatible line endings for Excel
  });

  // Generate filename
  let filename = 'HisabJourney_Ledger';
  if (filters.startDate && filters.endDate) {
    filename += `_${filters.startDate}_to_${filters.endDate}`;
  } else if (filters.startDate) {
    filename += `_from_${filters.startDate}`;
  }
  if (filters.accountId) {
    filename += `_${filters.accountId.replace(/\s+/g, '_')}`;
  }
  filename += '.csv';

  // Create and trigger download
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Export ledger data to Excel (.xlsx) format
 * Creates a properly formatted spreadsheet with styling
 */
export const exportToExcel = async (transactions, summary, filters, formatAmount, formatDate, exportConfig = {}) => {
  const currencyCode = exportConfig.currencyCode || 'INR';
  const currencyPrefix = getCurrencyPrefix(currencyCode);
  const accentColorHex = exportConfig.accentColor || '#3b82f6';

  // Format amount for Excel (plain number for calculations)
  const formatExcelAmount = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '';
    const num = Number(amount);
    if (isNaN(num)) return '';
    return num;
  };

  // Use user's date format from config (passed as formatDate parameter)
  const formatExportDate = (dateStr) => {
    if (!dateStr) return '';
    return formatDate(dateStr);
  };

  // Date range text
  let dateRangeText = 'All Transactions';
  if (filters.startDate && filters.endDate) {
    dateRangeText = `${formatExportDate(filters.startDate)} to ${formatExportDate(filters.endDate)}`;
  } else if (filters.startDate) {
    dateRangeText = `From ${formatExportDate(filters.startDate)}`;
  } else if (filters.endDate) {
    dateRangeText = `Until ${formatExportDate(filters.endDate)}`;
  }

  // Generated date/time using user's date format
  const now = new Date();
  const generatedDate = formatDate(now);
  const generatedTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

  // Build worksheet data
  const wsData = [];

  // ===== HEADER SECTION =====
  wsData.push(['HISAB JOURNEY - LEDGER REPORT', '', '', '', '', '', '', '', '']);
  wsData.push(['Personal Finance Manager', '', '', '', '', '', '', '', '']);
  wsData.push(['', '', '', '', '', '', '', '', '']);

  // Report info
  wsData.push(['Report Period:', dateRangeText, '', '', '', '', '', '', '']);
  wsData.push(['Account:', filters.accountId || 'All Accounts', '', '', '', '', '', '', '']);
  wsData.push(['Currency:', currencyPrefix.trim(), '', '', '', '', '', '', '']);
  wsData.push(['Generated:', `${generatedDate} at ${generatedTime}`, '', '', '', '', '', '', '']);
  wsData.push(['', '', '', '', '', '', '', '', '']);

  // ===== SUMMARY SECTION =====
  wsData.push(['FINANCIAL SUMMARY', '', '', '', '', '', '', '', '']);
  wsData.push(['Opening Balance:', formatExcelAmount(summary.openingBalance), '', 'Total Income:', formatExcelAmount(summary.totalIncome), '', 'Closing Balance:', formatExcelAmount(summary.closingBalance), '']);
  wsData.push(['Total Expense:', formatExcelAmount(summary.totalExpense), '', 'Net Change:', formatExcelAmount(summary.totalIncome - summary.totalExpense), '', '', '', '']);
  wsData.push(['', '', '', '', '', '', '', '', '']);

  // ===== TRANSACTION TABLE =====
  wsData.push([`TRANSACTION DETAILS (${transactions.length} records)`, '', '', '', '', '', '', '', '']);
  wsData.push(['', '', '', '', '', '', '', '', '']);

  // Table header row (row index 15)
  const headerRowIndex = wsData.length;
  wsData.push(['Date', 'Time', 'Description', 'Category', 'Account', 'Type', 'Debit', 'Credit', 'Balance']);

  // Opening balance row
  wsData.push([
    filters.startDate ? formatExportDate(filters.startDate) : '',
    '',
    'Opening Balance',
    '',
    '',
    '',
    '',
    '',
    formatExcelAmount(summary.openingBalance)
  ]);

  // Transaction rows
  transactions.forEach((tx) => {
    const accountDisplay = tx.type === 'transfer'
      ? `${tx.bankName || ''} → ${tx.toAccount || ''}`
      : (tx.bankName || '');

    wsData.push([
      formatExportDate(tx.date),
      tx.time || '',
      tx.description || '',
      tx.category || '',
      accountDisplay,
      tx.type || '',
      tx.debit > 0 ? formatExcelAmount(tx.debit) : '',
      tx.credit > 0 ? formatExcelAmount(tx.credit) : '',
      formatExcelAmount(tx.balance)
    ]);
  });

  // Closing balance row
  wsData.push([
    filters.endDate ? formatExportDate(filters.endDate) : formatDate(new Date()),
    '',
    'Closing Balance',
    '',
    '',
    '',
    '',
    '',
    formatExcelAmount(summary.closingBalance)
  ]);

  wsData.push(['', '', '', '', '', '', '', '', '']);
  wsData.push(['END OF REPORT', '', '', '', '', '', '', '', '']);
  wsData.push(['Generated by Hisab Journey - Personal Finance Manager', '', '', '', '', '', '', '', '']);

  // Create worksheet
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Set column widths
  ws['!cols'] = [
    { wch: 12 },  // Date
    { wch: 8 },   // Time
    { wch: 35 },  // Description
    { wch: 15 },  // Category
    { wch: 20 },  // Account
    { wch: 10 },  // Type
    { wch: 14 },  // Debit
    { wch: 14 },  // Credit
    { wch: 14 }   // Balance
  ];

  // Merge cells for header
  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 8 } },  // Title row
    { s: { r: 1, c: 0 }, e: { r: 1, c: 8 } },  // Subtitle row
    { s: { r: 8, c: 0 }, e: { r: 8, c: 8 } },  // Summary header
    { s: { r: 13, c: 0 }, e: { r: 13, c: 8 } } // Transaction details header
  ];

  // Create workbook and add worksheet
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Ledger');

  // Generate filename with datetime format: hisab-journey-ledger-YYYYMMDDHHMMSS.xlsx
  const filename = generateFilename('xlsx');

  // Save file - handles both web and mobile
  console.log('[Export] Excel export - isNativePlatform:', isNativePlatform());
  if (isNativePlatform()) {
    // For mobile, write to array buffer and use downloadFile
    console.log('[Export] Generating Excel buffer for mobile...');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    console.log('[Export] Excel buffer type:', excelBuffer?.constructor?.name, 'length:', excelBuffer?.length);
    await downloadFile(filename, excelBuffer, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
    console.log('[Export] Excel download completed');
  } else {
    XLSX.writeFile(wb, filename);
  }
};

/**
 * Export all ledger data (fetches all pages for export)
 */
export const fetchAllForExport = async (apiFunction, params) => {
  const response = await apiFunction({
    ...params,
    page: 1,
    limit: 10000
  });
  return response;
};
